import { HttpErrorResponse } from '@angular/common/http';
import { HttpErrorCodes } from '../../api/enums/http-error-codes.enum';

/**
 * One place that turns a failed response into something worth showing a person.
 *
 * Shared by `errorInterceptor` and by the 2FA replay in `twoFactorInterceptor` — the latter sends
 * its retry from inside the chain, so those errors never travel back through `errorInterceptor`
 * and would otherwise surface no message at all. (AML-1056)
 */
export function httpErrorMessage(error: HttpErrorResponse): string {
    // A deactivated admin still gets a token from `api/admin/login`; only the first data call comes
    // back `401 {"code":33,"message":null}`. An invalid or missing token returns 401 with an empty
    // body, so code 33 is what separates "your account was switched off" from "your session ended".
    // Either way a 401 ends in a redirect to /login (AuthInterceptor), so explain that, not the call.
    // (AML-1058)
    if (error.status === 401) {
        return error.error?.code === HttpErrorCodes.AdminDeactivated
            ? 'Your administrator account has been deactivated. Contact another administrator to restore access.'
            : 'Your session has ended. Please sign in again.';
    }

    // The backend explains most refusals in `error.error.message` ("You cannot deactivate your own
    // admin account" for the 409 in AML-1056, for one). The toast used to show Angular's own
    // `error.message` instead — "Http failure response for <url>: 409 Conflict" — dropping a
    // perfectly good explanation in favour of a status code.
    const message = (error.error as { message?: unknown } | null)?.message;
    if (typeof message === 'string' && message.trim()) return message.trim();

    return 'Oops, something went wrong';
}
